import { Accordion, type Theme } from "@mui/material";
import { useSearchParams } from "Navigation/useSearchParams";
import type { ProductPricingsType } from "Components/Hunter/Merchants/types";
import { AccordionContent } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/AccordionContent";
import { AccordionHeader } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionHeader/AccordionHeader";
import type { MerchantDetailDto, ProductCode } from "Infrastructure/Api/Api";
import type { AppProductsVariant } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import type { PrerequisiteItem } from "Components/Shared/Merchant/Products/AppProducts/types";

type Props = {
  code: ProductCode;
  merchantPublicID: string;
  merchantData: MerchantDetailDto;
  allGroupedPricings: ProductPricingsType;
  variant: AppProductsVariant;
  isActive?: boolean;
  isDisabled?: boolean;
  isDefault?: boolean;
  isOnboarding?: boolean;
  prerequisites?: Array<PrerequisiteItem>;
};

export const ProductAccordion = ({
  code,
  merchantData,
  merchantPublicID,
  allGroupedPricings,
  variant,
  isDisabled = false,
  isActive = false,
  isDefault = false,
  isOnboarding,
  prerequisites,
}: Props) => {
  const [searchParams] = useSearchParams();
  // Optional deep-link from K ověření / similar admin lists: ?expandProduct=OrbitL forces this
  // accordion open on first render so admins land directly on the product they came in for.
  const isExpandedByQuery = searchParams.get("expandProduct") === code;

  return (
    <Accordion
      disabled={isDisabled}
      defaultExpanded={
        (isDefault && variant === "editable") || isExpandedByQuery
      }
      disableGutters
      elevation={0}
      sx={({ palette }: Theme) => ({
        borderRadius: 1,
        borderBottom: 0,
        backgroundColor: "background.paper",
        "&:last-child": { borderBottom: `1px solid ${palette.divider}` },
        "&::before": { display: "none" },
        "&.Mui-disabled": { backgroundColor: "action.hover" },
      })}
    >
      <AccordionHeader code={code} isActive={isActive} isDefault={isDefault} />
      <AccordionContent
        code={code}
        isActive={isActive}
        isDefault={isDefault}
        variant={variant}
        merchantPublicID={merchantPublicID}
        merchantData={merchantData}
        allGroupedPricings={allGroupedPricings}
        isOnboarding={isOnboarding}
        prerequisites={prerequisites}
      />
    </Accordion>
  );
};
